import axios from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useMsal } from '@azure/msal-react';
import { CircularProgress, IconButton } from '@mui/material';
import { useMutation } from '@tanstack/react-query';

import { BackIcon } from '../../../../assets/svg';
import { PyButton, TOASTER_TYPE, ToastMessage } from '../../../../components';
import {
    applyRewardToCartItems, applyRewardToTotalCart
} from '../../../../services/applyReward.service';
import { RewardRuleEffects } from '../../../../types';
import { convertFromNairaToKobo } from '../../../../utils/helper';
import { CartItem, MerchantHeader } from '../../components';
import { CHECKOUT_SESSION_KEY, usePayLinkCart } from '../../context/PayLinkCartContext';
import { usePayLinkUser } from '../../context/PaylinkUserContext';
import { useLoyaltyEnrolment } from '../../hook';
import { openWalletSession } from '../../services/api';
import { IOpenSessionPayload } from '../../types/walllet';
import { formatPaylinkCart } from '../../utils/helper';
import { CartSummary } from './components/CartSummary';
import { CheckoutButton } from './components/CheckoutButton';
import { PaymentMethod } from './components/PaymentMethod';
import { Rewards } from './components/reward/Reward';
import { SelectedPaymentMethod } from './components/SelectedPaymentMethod';
import { ShippingForm } from './components/ShippingForm';

const CHECKOUT_SESSION_OPENING_KEY = `${CHECKOUT_SESSION_KEY}_opening`;

const isCheckoutSessionOpening = () =>
  localStorage.getItem(CHECKOUT_SESSION_OPENING_KEY) === "1";

const setCheckoutSessionOpening = (isOpening: boolean) => {
  if (isOpening) {
    localStorage.setItem(CHECKOUT_SESSION_OPENING_KEY, "1");
    return;
  }

  localStorage.removeItem(CHECKOUT_SESSION_OPENING_KEY);
};

export const PayLinkCheckout = () => {
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const { user } = usePayLinkUser();
  const {
    storeProfile,
    cartItems,
    sessionUniqueReferenceNumber,
    setSessionUniqueReferenceNumber,
    merchantSessionUrn,
  } = usePayLinkCart();
  const {
    reward,
    redeemedPoints,
    shippingRate,
    freeShippingReward,
    cardToken,
  } = usePayLinkUser();
  const [currentView, setCurrentView] = useState<number>(0);
  const [isRewardEnrollmentDone, setIsRewardEnrollmentDone] =
    useState<boolean>(false);
  const isAuthenticated = !!accounts && accounts.length >= 1;
  const isCartEmpty = (cartItems?.length ?? 0) === 0;
  const showMobileStickyAuthButton = !isAuthenticated && currentView === 0;
  const onOpenWalletSession = useMutation({
    mutationKey: ["OpenWalletSession"],
    mutationFn: openWalletSession,
  });

  useEffect(() => {
    if (
      accounts.length > 0 &&
      user &&
      storeProfile &&
      !sessionUniqueReferenceNumber &&
      !isCheckoutSessionOpening()
    ) {
      void handleOpenWalletSession();
    }
  }, [accounts.length, user, storeProfile, sessionUniqueReferenceNumber]);

  const storeLink = storeProfile?.slug
    ? `/paylink/s/${storeProfile.slug}`
    : null;

  const handleOpenWalletSession = async () => {
    if (
      !user ||
      !storeProfile ||
      !!sessionUniqueReferenceNumber ||
      isCheckoutSessionOpening()
    )
      return;

    const sessionUrn = merchantSessionUrn;
    setCheckoutSessionOpening(true);

    try {
      const payload: IOpenSessionPayload = {
        uniqueReferenceNumber: sessionUrn,
        walletUniqueReferenceNumber: user?.walletUniqueReferenceNumber,
        merchantUniqueReferenceNumber:
          storeProfile?.customerUniqueReferenceNumber,
        consumerUniqueReferenceNumber: user?.consumerUniqueReferenceNumber,
        cart: formatPaylinkCart(
          cartItems.map((item) => ({
            ...item,
            image: item.productImages[0].documentAbsoluteUri,
          })),
        ).map((item) => ({
          ...item,
          price: convertFromNairaToKobo(item.price),
        })),
      };

      const res = await onOpenWalletSession.mutateAsync(payload);

      if (res.status) {
        setSessionUniqueReferenceNumber(res.data.uniqueReferenceNumber);
        localStorage.setItem(
          CHECKOUT_SESSION_KEY,
          res.data.uniqueReferenceNumber,
        );
      }
    } catch (error) {
      const status = axios.isAxiosError(error)
        ? error.response?.status
        : undefined;

      if (status === 409) {
        const existingSessionUrn =
          localStorage.getItem(CHECKOUT_SESSION_KEY) || sessionUrn;

        if (existingSessionUrn) {
          setSessionUniqueReferenceNumber(existingSessionUrn);
          localStorage.setItem(CHECKOUT_SESSION_KEY, existingSessionUrn);
          return;
        }
      }

      console.log(error);
      ToastMessage(
        TOASTER_TYPE.ERROR,
        "Unable to start checkout right now. Please try again.",
      );
    } finally {
      setCheckoutSessionOpening(false);
    }
  };

  const handleNextView = () => {
    setCurrentView(currentView + 1);
  };

  const handleAuthenticateToContinue = () => {
    localStorage.setItem("redirectUrl", "/paylink/session");
    localStorage.setItem("loginFlow", "paylink");
    navigate("/paylink/login");
  };

  const handleBack = () => {
    if (currentView === 0) {
      if (storeLink) {
        navigate(storeLink);
      }
    } else {
      setCurrentView(currentView - 1);
    }
  };

  useEffect(() => {
    if (isAuthenticated && isCartEmpty && currentView > 0) {
      setCurrentView(0);
    }
  }, [currentView, isAuthenticated, isCartEmpty]);

  const currentViewTitles = [
    `Cart`,
    "Shipping rate & payment method",
    "Rewards",
    "Order summary",
  ];

  const mobileOrderingClass1 = currentView > 0 ? "!order-2 lg:!order-1" : "";
  const mobileOrderingClass2 = currentView > 0 ? "!order-1 lg:!order-2" : "";

  const { cartTotal, rewardDiscount, pointDiscount, shippingFee, amountToPay } =
    useMemo(() => {
      const rawCartTotal = (cartItems || []).reduce(
        (sum, item) =>
          sum + Number(item.price || 0) * Number(item.quantity || 0),
        0,
      );

      const normalizedCartItems = (cartItems || []).map((item: any) => ({
        ...item,
        newPrice: Number(item.price || 0),
        newQuantity: Number(item.quantity || 0),
        rewardType: item.rewardType || "",
      }));

      const cartWithItemReward = reward
        ? applyRewardToCartItems(reward, normalizedCartItems)
        : normalizedCartItems;

      const itemLevelDiscount = (cartWithItemReward || []).reduce(
        (sum: number, item: any) => {
          const oldPrice = Number(item.price || 0);
          const newPrice = Number(item.newPrice ?? oldPrice);
          const quantity = Number(item.quantity || 0);
          const discountPerUnit = Math.max(0, oldPrice - newPrice);
          return sum + discountPerUnit * quantity;
        },
        0,
      );

      const totalLevelReward = reward
        ? applyRewardToTotalCart(reward, rawCartTotal)
        : { price: 0 };

      const ruleBasedDiscount =
        itemLevelDiscount > 0
          ? itemLevelDiscount
          : Number(totalLevelReward?.price || 0);

      // Fallback for sessionRewardsApplicable rewards that have no rewardRules
      // (server pre-evaluated them, so apply rewardEffectValue directly)
      const appliedRewardDiscount =
        ruleBasedDiscount > 0
          ? ruleBasedDiscount
          : (() => {
              if (reward?.rewardEffect === RewardRuleEffects.AddDiscount) {
                const effectValue = Number(reward.rewardEffectValue ?? 0);
                if (effectValue <= 0) return 0;
                const rawDiscount = rawCartTotal * effectValue;
                const limit = Number(reward.rewardEffectValueLimit ?? 0) / 100;
                return limit > 0 ? Math.min(rawDiscount, limit) : rawDiscount;
              }
              return 0;
            })();

      console.log("shippingRate", shippingRate);

      const appliedPointDiscount = Math.max(0, Number(redeemedPoints || 0));
      const shippingDiscount = freeShippingReward
        ? Math.min(
            shippingRate?.charge ?? 0,
            Number((freeShippingReward.rewardEffectValueLimit ?? 0) / 100),
          )
        : 0;

      console.log("shippingDiscouefuwfirfbuyrbufuuount", shippingDiscount);
      const effectiveShipping =
        Math.max(0, shippingRate?.charge ?? 0) - (shippingDiscount || 0);
      console.log("effectiveShipping", effectiveShipping);
      const _shippingFee = effectiveShipping;

      const subtotalAfterDiscounts = Math.max(
        0,
        rawCartTotal - appliedRewardDiscount - appliedPointDiscount,
      );
      const totalPayable = (subtotalAfterDiscounts + effectiveShipping).toFixed(
        2,
      );

      console.log("shippingDiscount", shippingDiscount);

      console.log("_shippingFee", _shippingFee);

      console.log({
        subtotalAfterDiscounts,
        effectiveShipping,
        totalPayable,
        appliedRewardDiscount,
      });

      return {
        cartTotal: rawCartTotal,
        rewardDiscount: appliedRewardDiscount,
        pointDiscount: appliedPointDiscount,
        shippingFee: _shippingFee,
        amountToPay: parseFloat(totalPayable),
      };
      }, [cartItems, reward, redeemedPoints, shippingRate, freeShippingReward]);

  const ViewRewardButton = () => (
    <PyButton
      click={handleNextView}
      isDisabled={!cardToken || isCartEmpty}
      extraClass="!px-8 !w-full lg:!w-fit "
      type="submit">
      View available rewards
    </PyButton>
  );

  if (
    (onOpenWalletSession.isLoading || isCheckoutSessionOpening()) &&
    !sessionUniqueReferenceNumber
  ) {
    return (
      <div className="!py-20 flex flex-col !gap-4 items-center justify-center">
        <CircularProgress />
        <p className="text-sm text-py_black-400">
          Preparing your cart for checkout...
        </p>
      </div>
    );
  }

  return (
    <section
      className={`flex flex-col w-full !container ${
        showMobileStickyAuthButton ? "!pb-28 lg:!pb-10" : "!pb-10"
      }`}>
      {currentView === 0 && <MerchantHeader />}

      <div className="!p-5 lg:!mt-6 inline-flex items-center gap-2 text-lg text-py_black-950 font-medium leading-[120%] tracking-[-0.02em]">
        <IconButton onClick={handleBack} className="!p-1 !rounded-md !w-fit">
          <BackIcon />
        </IconButton>
        {currentViewTitles[currentView]}
      </div>

      <section className="grid grid-cols-2 !px-5 lg:!px-0 !gap-y-6 lg:!gap-20">
        {currentView === 0 && (
          <>
            {/* Cart items section */}
            <div className="flex flex-col gap-4 col-span-2 lg:col-span-1">
              <CartItem />
            </div>

            {/* Cart summary */}
            <div className="flex flex-col gap-6 col-span-2 lg:col-span-1">
              <CartSummary
                currentView={currentView}
                cartTotal={cartTotal}
                rewardDiscount={rewardDiscount}
                pointDiscount={pointDiscount}
                shippingFee={shippingFee}
                amountToPay={amountToPay}
              />

              {isAuthenticated && sessionUniqueReferenceNumber ? (
                <ShippingForm handleNextView={handleNextView} isDisabled={isCartEmpty} />
              ) : (
                <div className="hidden lg:flex flex-col gap-3 border-t border-py_black-100 !pt-6">
                  <PyButton
                    click={handleAuthenticateToContinue}
                    isDisabled={isCartEmpty}
                    extraClass="!px-8 !w-full lg:!w-fit">
                    Continue to checkout
                  </PyButton>
                </div>
              )}
            </div>
          </>
        )}

        {currentView === 1 && (
          <>
            {/* Payment method */}
            <div className={`${mobileOrderingClass1} col-span-2 lg:col-span-1`}>
              <PaymentMethod isDisabled={isCartEmpty} />

              <div className="!mt-10 lg:hidden">
                <ViewRewardButton />
              </div>
            </div>

            {/* Cart summary */}
            <div
              className={`${mobileOrderingClass2} flex flex-col gap-6 col-span-2 lg:col-span-1`}>
              <CartSummary
                currentView={currentView}
                cartTotal={cartTotal}
                rewardDiscount={rewardDiscount}
                pointDiscount={pointDiscount}
                shippingFee={shippingFee}
                amountToPay={amountToPay}
              />

              <div className="hidden lg:block">
                <ViewRewardButton />
              </div>
            </div>
          </>
        )}

        {currentView === 2 && (
          <>
            {/* Reward */}
            <div className="col-span-2 lg:col-span-1">
              <Rewards
                handleNextView={handleNextView}
                totalAmount={cartTotal}
                isEnrollmentDone={isRewardEnrollmentDone}
                setIsEnrollmentDone={setIsRewardEnrollmentDone}
                isDisabled={isCartEmpty}
              />
            </div>
          </>
        )}

        {currentView === 3 && (
          <>
            {/* Payment method */}
            <div
              className={`${mobileOrderingClass1} flex flex-col !gap-6 col-span-2 lg:col-span-1`}>
              <SelectedPaymentMethod />
              <hr className="h-[1px] bg-py_black-50 lg:hidden" />
              <div className="!mt-10 lg:hidden">
                <CheckoutButton
                  amountToPay={amountToPay}
                  rewardDiscount={rewardDiscount}
                />
              </div>
            </div>

            {/* Cart summary */}
            <div
              className={`${mobileOrderingClass2} flex flex-col gap-6 col-span-2 lg:col-span-1`}>
              <CartSummary
                currentView={currentView}
                cartTotal={cartTotal}
                rewardDiscount={rewardDiscount}
                pointDiscount={pointDiscount}
                shippingFee={shippingFee}
                amountToPay={amountToPay}
              />
              <div className="hidden lg:block">
                <CheckoutButton
                  amountToPay={amountToPay}
                  rewardDiscount={rewardDiscount}
                />
              </div>
            </div>
          </>
        )}
      </section>

      {showMobileStickyAuthButton && (
        <div className="fixed bottom-0 left-0 right-0 z-20 bg-white border-t border-py_black-100 !p-4 lg:hidden">
          <div className="!container">
            <PyButton
              click={handleAuthenticateToContinue}
              isDisabled={isCartEmpty}
              extraClass="!px-8 !w-full">
              Continue to checkout
            </PyButton>
          </div>
        </div>
      )}
    </section>
  );
};
